import { useMemo } from "react";

import type { UseQueryResult } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import { selectCountriesFilterValue } from "app/pages/sales-intelligence/modules/leads-search-filters";

import { selectIsAggregatedDataUsed } from "../../store/selectors";
import { createDetailedResultsRequestDto } from "../../store/utils";
import type {
    LeadsSearchRow,
    LeadDetailedResultsRequestDto,
    LeadsDetailedResultsResponseDto,
    LeadsSearchRequestDto,
    LeadsType,
} from "../../types";

import useDomainsDetailedResultsQuery from "./useDomainsDetailedResultsQuery";

type QueryParams = {
    leadsType: LeadsType;
    dto: LeadsSearchRequestDto;
    rows: readonly LeadsSearchRow[];
};

export default function useDomainsDetailedResultsQueryWithDto(
    { leadsType, dto, rows }: QueryParams,
    enabled = true,
): UseQueryResult<LeadsDetailedResultsResponseDto> {
    const isAggregatedDataUsed = useSelector(selectIsAggregatedDataUsed);
    const countries = useSelector(selectCountriesFilterValue);

    const shouldFetchDomainsDetailedResults = [enabled, isAggregatedDataUsed, (countries ?? []).length > 1].every(
        Boolean,
    );

    const domainsDetailedResultsDto = useMemo((): LeadDetailedResultsRequestDto => {
        if (!shouldFetchDomainsDetailedResults) {
            return {} as LeadDetailedResultsRequestDto;
        }

        return createDetailedResultsRequestDto(dto, rows);
    }, [dto, rows, shouldFetchDomainsDetailedResults]);

    return useDomainsDetailedResultsQuery(leadsType, domainsDetailedResultsDto, {
        enabled: shouldFetchDomainsDetailedResults,
    });
}
